import { tokens } from './tokens'

/**
 * Global CSS styles for SovereignScope application
 */
export const globalStyles = `
  /* Inter font import */
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  /* CSS Custom Properties for Design Tokens */
  :root {
    /* Risk Colors */
    --color-risk-critical: ${tokens.colors.risk.critical};
    --color-risk-high: ${tokens.colors.risk.high};
    --color-risk-medium: ${tokens.colors.risk.medium};
    --color-risk-low: ${tokens.colors.risk.low};
    --color-risk-unknown: ${tokens.colors.risk.unknown};
    
    /* Confidence Colors */
    --color-confidence-high: ${tokens.colors.confidence.high};
    --color-confidence-medium: ${tokens.colors.confidence.medium};
    --color-confidence-low: ${tokens.colors.confidence.low};
    --color-confidence-insufficient: ${tokens.colors.confidence.insufficient};
    
    /* Brand Colors */
    --color-brand-primary: ${tokens.colors.brand.primary};
    --color-brand-secondary: ${tokens.colors.brand.secondary};
    --color-brand-background: ${tokens.colors.brand.background};
    --color-brand-surface: ${tokens.colors.brand.surface};
    --color-brand-text-primary: ${tokens.colors.brand.textPrimary};
    --color-brand-text-secondary: ${tokens.colors.brand.textSecondary};
    --color-brand-text-muted: ${tokens.colors.brand.textMuted};
    
    /* Semantic Colors */
    --color-semantic-success: ${tokens.colors.semantic.success};
    --color-semantic-warning: ${tokens.colors.semantic.warning};
    --color-semantic-error: ${tokens.colors.semantic.error};
    --color-semantic-info: ${tokens.colors.semantic.info};
    
    /* Typography */
    --font-family-primary: ${tokens.typography.fontFamily.primary};
    --font-family-mono: ${tokens.typography.fontFamily.mono};
    
    /* Spacing */
    --spacing-1: ${tokens.spacing[1]};
    --spacing-2: ${tokens.spacing[2]};
    --spacing-3: ${tokens.spacing[3]};
    --spacing-4: ${tokens.spacing[4]};
    --spacing-5: ${tokens.spacing[5]};
    --spacing-6: ${tokens.spacing[6]};
    --spacing-8: ${tokens.spacing[8]};
    --spacing-10: ${tokens.spacing[10]};
    --spacing-12: ${tokens.spacing[12]};
    --spacing-16: ${tokens.spacing[16]};
    --spacing-20: ${tokens.spacing[20]};
    --spacing-24: ${tokens.spacing[24]};
    --spacing-32: ${tokens.spacing[32]};
    
    /* Shadows */
    --shadow-sm: ${tokens.shadows.sm};
    --shadow-base: ${tokens.shadows.base};
    --shadow-md: ${tokens.shadows.md};
    --shadow-lg: ${tokens.shadows.lg};
    --shadow-xl: ${tokens.shadows.xl};
    
    /* Border Radius */
    --radius-sm: ${tokens.borderRadius.sm};
    --radius-base: ${tokens.borderRadius.base};
    --radius-md: ${tokens.borderRadius.md};
    --radius-lg: ${tokens.borderRadius.lg};
    --radius-xl: ${tokens.borderRadius.xl};
    --radius-2xl: ${tokens.borderRadius['2xl']};
    --radius-full: ${tokens.borderRadius.full};
  }
  
  /* CSS Reset and Base Styles */
  *, *::before, *::after {
    box-sizing: border-box;
  }
  
  * {
    margin: 0;
    padding: 0;
  }
  
  html {
    font-size: 16px;
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }
  
  body {
    font-family: var(--font-family-primary);
    font-weight: ${tokens.typography.fontWeight.normal};
    color: var(--color-brand-text-primary);
    background-color: var(--color-brand-background);
    line-height: ${tokens.typography.lineHeight.normal};
    min-height: 100vh;
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }
  
  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }
  
  /* Typography Reset */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${tokens.typography.fontWeight.semibold};
    line-height: ${tokens.typography.lineHeight.tight};
    color: var(--color-brand-text-primary);
  }
  
  h1 { font-size: ${tokens.typography.fontSize['3xl']}; }
  h2 { font-size: ${tokens.typography.fontSize['2xl']}; }
  h3 { font-size: ${tokens.typography.fontSize.xl}; }
  h4 { font-size: ${tokens.typography.fontSize.lg}; }
  h5 { font-size: ${tokens.typography.fontSize.base}; }
  h6 { font-size: ${tokens.typography.fontSize.sm}; }
  
  p {
    color: var(--color-brand-text-secondary);
    line-height: ${tokens.typography.lineHeight.relaxed};
  }
  
  /* Interactive Elements */
  button {
    font-family: inherit;
    cursor: pointer;
    border: none;
    background: transparent;
  }
  
  button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
  
  a {
    color: var(--color-brand-primary);
    text-decoration: none;
  }
  
  a:hover {
    text-decoration: underline;
  }
  
  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }
  
  /* Focus Management */
  :focus {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }
  
  :focus:not(:focus-visible) {
    outline: none;
  }
  
  /* Screen Reader Only Content */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  
  /* High Contrast Mode Support */
  @media (prefers-contrast: high) {
    :root {
      --color-brand-primary: #000000;
      --color-brand-text-primary: #000000;
      --color-brand-text-secondary: #000000;
    }
  }
  
  /* Reduced Motion Support */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
  
  /* Dark Mode Support (Future) */
  @media (prefers-color-scheme: dark) {
    /* Dark mode tokens will be added here when implemented */
  }
`
